import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAppStore } from '../stores/useAppStore';
import { chats as chatStore } from '../storage/db';
import {
  startChatTurn,
  resumeChatTurn,
  findPendingWrites,
  summarizeChatTitle,
  type PendingWrite,
} from '../pipeline/chatTurn';
import { summarizeToolUse } from '../pipeline/chatTools';
import ApprovalCard from '../components/ApprovalCard';
import { newStoryFromSlug, runAutoPipeline } from '../pipeline/stages';
import { isSyncConfigured } from '../lib/syncClient';
import { useWakeLock } from '../lib/useWakeLock';
import { VIBE_LABELS } from '../types';
import type {
  ChatAssistantBlock,
  ChatMessage,
  ChatToolResultBlock,
  ChatUserBlock,
  Slug,
  Story,
} from '../types';
import './routes.css';

function relTime(ms: number): string {
  const diff = Date.now() - ms;
  if (diff < 60_000) return 'just now';
  const min = Math.floor(diff / 60_000);
  if (min < 60) return `${min}m`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h`;
  const days = Math.floor(hr / 24);
  return `${days}d`;
}

export default function ChatRoute() {
  const { chatId } = useParams();
  const activeUniverseId = useAppStore((s) => s.ui.activeUniverseId);
  const universes = useAppStore((s) => s.universes);
  const universe = activeUniverseId ? universes[activeUniverseId] : undefined;
  const nav = useNavigate();

  if (!universe) {
    return (
      <div className="route">
        <h1>Chat</h1>
        <div className="dim">No active universe. Pick one from the home screen.</div>
        <button onClick={() => nav('/')}>Go to universes</button>
      </div>
    );
  }

  if (!chatId) {
    return <ChatListView universeId={universe.id} />;
  }
  return <ChatThreadView key={chatId} chatId={chatId} universeId={universe.id} />;
}

function ChatListView({ universeId }: { universeId: string }) {
  const chatsMap = useAppStore((s) => s.chats);
  const universesMap = useAppStore((s) => s.universes);
  const createChat = useAppStore((s) => s.createChat);
  const deleteChat = useAppStore((s) => s.deleteChat);
  const renameChat = useAppStore((s) => s.renameChat);
  const updateChatUniverse = useAppStore((s) => s.updateChatUniverse);
  const nav = useNavigate();

  const allChats = useMemo(() => Object.values(chatsMap), [chatsMap]);

  const list = useMemo(
    () =>
      allChats
        .filter((c) => c.universeId === universeId)
        .sort((a, b) => b.lastUsedAt - a.lastUsedAt),
    [allChats, universeId]
  );

  const otherUniverseChats = useMemo(
    () =>
      allChats.filter(
        (c) => c.universeId !== universeId && universesMap[c.universeId]
      ),
    [allChats, universeId, universesMap]
  );

  const orphanedChats = useMemo(
    () =>
      allChats
        .filter((c) => !universesMap[c.universeId])
        .sort((a, b) => b.lastUsedAt - a.lastUsedAt),
    [allChats, universesMap]
  );

  const [orphanFirstMessage, setOrphanFirstMessage] = useState<
    Record<string, string>
  >({});
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const next: Record<string, string> = {};
      for (const c of orphanedChats) {
        try {
          const msgs = (await chatStore.get(c.id)) ?? [];
          for (const m of msgs) {
            if (m.role !== 'user') continue;
            const block = m.content.find((b) => b.type === 'text');
            if (block && block.type === 'text' && block.text.trim()) {
              next[c.id] = block.text;
              break;
            }
          }
        } catch (e) {
          console.warn('orphan first-message read failed', c.id, e);
        }
      }
      if (!cancelled) setOrphanFirstMessage(next);
    })();
    return () => {
      cancelled = true;
    };
  }, [orphanedChats]);

  function onNew() {
    const chat = createChat(universeId);
    nav(`/chat/${chat.id}`);
  }

  function onDelete(id: string, title: string) {
    if (!confirm(`Delete chat "${title}"? This cannot be undone.`)) return;
    void chatStore.del(id);
    deleteChat(id);
  }

  function onRename(id: string, oldTitle: string) {
    const next = prompt('Rename chat', oldTitle);
    if (next == null) return;
    renameChat(id, next);
  }

  function onAdopt(id: string) {
    updateChatUniverse(id, universeId);
  }

  return (
    <div className="route">
      <h1>Chat</h1>
      <div className="dim small">
        Talk through canon, prompts, voice, and identity for this universe. Ask
        the assistant to read or revise — it'll show you each change before it's
        applied.
      </div>
      <button className="primary" onClick={onNew}>
        + New chat
      </button>
      {list.length === 0 ? (
        <div className="dim">
          No chats yet for this universe.
          {otherUniverseChats.length > 0 && (
            <>
              {' '}
              {otherUniverseChats.length} chat
              {otherUniverseChats.length === 1 ? '' : 's'} exist in other
              universes.
            </>
          )}
        </div>
      ) : (
        <div className="chat-list">
          {list.map((c) => (
            <div key={c.id} className="chat-list-row">
              <button
                className="chat-list-open"
                onClick={() => nav(`/chat/${c.id}`)}
              >
                <div className="chat-list-title">{c.title}</div>
                <div className="dim small">
                  {c.messageCount} message{c.messageCount === 1 ? '' : 's'} ·{' '}
                  {relTime(c.lastUsedAt)}
                </div>
              </button>
              <div className="chat-list-actions">
                <button className="ghost" onClick={() => onRename(c.id, c.title)}>
                  Rename
                </button>
                <button
                  className="ghost"
                  onClick={() => onDelete(c.id, c.title)}
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {orphanedChats.length > 0 && (
        <section className="card">
          <h2>Orphaned chats</h2>
          <div className="dim small">
            These chats reference a universe that no longer exists. Adopt one
            into this universe to keep using it, or delete it.
          </div>
          <div className="chat-list">
            {orphanedChats.map((c) => (
              <div key={c.id} className="chat-list-row orphan-row">
                <button
                  className="chat-list-open"
                  onClick={() => nav(`/chat/${c.id}`)}
                >
                  <div className="chat-list-title">{c.title}</div>
                  <div className="dim small">
                    {c.messageCount} message{c.messageCount === 1 ? '' : 's'} ·{' '}
                    {relTime(c.lastUsedAt)} · missing universe{' '}
                    <code>{c.universeId.slice(0, 6)}</code>
                  </div>
                  {orphanFirstMessage[c.id] && (
                    <pre className="orphan-first-message">
                      {orphanFirstMessage[c.id]}
                    </pre>
                  )}
                </button>
                <div className="chat-list-actions">
                  <button className="ghost" onClick={() => onAdopt(c.id)}>
                    Adopt here
                  </button>
                  <button
                    className="ghost"
                    onClick={() => onDelete(c.id, c.title)}
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

function ChatThreadView({
  chatId,
  universeId,
}: {
  chatId: string;
  universeId: string;
}) {
  const chat = useAppStore((s) => s.chats[chatId]);
  const touchChat = useAppStore((s) => s.touchChat);
  const renameChat = useAppStore((s) => s.renameChat);
  const slugPool = useAppStore((s) => s.slugPool);
  const stories = useAppStore((s) => s.stories);
  const settings = useAppStore((s) => s.settings);
  const universes = useAppStore((s) => s.universes);
  const createStory = useAppStore((s) => s.createStory);
  const hideSlug = useAppStore((s) => s.hideSlug);
  const setActiveStoryForUniverse = useAppStore(
    (s) => s.setActiveStoryForUniverse
  );
  const nav = useNavigate();

  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [composer, setComposer] = useState('');
  const [busy, setBusy] = useState(false);
  const [pending, setPending] = useState<PendingWrite[]>([]);
  const [resolutions, setResolutions] = useState<Map<string, boolean>>(
    new Map()
  );
  const [error, setError] = useState<string | undefined>();
  const [errorBySlug, setErrorBySlug] = useState<Record<string, string>>({});
  const abortRef = useRef<AbortController | undefined>(undefined);
  const bottomRef = useRef<HTMLDivElement | null>(null);

  const storyBySlug = useMemo(() => {
    const m = new Map<string, Story>();
    for (const s of Object.values(stories)) m.set(s.slug.id, s);
    return m;
  }, [stories]);

  const autoActive = useMemo(
    () =>
      Object.values(stories).some(
        (s) => s.universeId === universeId && s.inFlight !== undefined
      ),
    [stories, universeId]
  );
  useWakeLock(busy || autoActive, 'chat');

  const universe = universes[universeId];
  const canGenerate = isSyncConfigured();
  const hasVoice = !!universe?.defaultVoiceId;
  const canAuto = canGenerate && hasVoice;

  function onPickSlug(slugId: string) {
    const slug = slugPool.find((s) => s.id === slugId);
    if (!slug) return;
    const existing = storyBySlug.get(slugId);
    const story = existing ?? newStoryFromSlug(slug);
    if (!existing) createStory(story);
    setActiveStoryForUniverse(slug.universeId, story.id);
    hideSlug(slug.id);
    nav('/workspace');
  }

  async function onAutoSlug(slugId: string) {
    const slug = slugPool.find((s) => s.id === slugId);
    if (!slug) return;
    const existing = storyBySlug.get(slugId);
    const story = existing ?? newStoryFromSlug(slug);
    if (!existing) createStory(story);
    setErrorBySlug((m) => {
      const { [slugId]: _drop, ...rest } = m;
      return rest;
    });
    try {
      await runAutoPipeline({ story, settings });
      hideSlug(slug.id);
    } catch (e) {
      setErrorBySlug((m) => ({ ...m, [slugId]: (e as Error).message }));
    }
  }

  function openInWorkspace(storyId: string) {
    setActiveStoryForUniverse(universeId, storyId);
    nav('/workspace');
  }

  // Load messages on mount / chatId change.
  useEffect(() => {
    let cancelled = false;
    chatStore
      .get(chatId)
      .then((m) => {
        if (cancelled) return;
        const arr = m ?? [];
        setMessages(arr);
        setPending(findPendingWrites(arr));
        setLoaded(true);
      })
      .catch((e) => {
        if (cancelled) return;
        setError((e as Error).message);
        setLoaded(true);
      });
    return () => {
      cancelled = true;
      abortRef.current?.abort();
    };
  }, [chatId]);

  // Reset resolutions when the pending set changes.
  const pendingKey = pending.map((p) => p.tool_use_id).join('|');
  useEffect(() => {
    setResolutions(new Map());
  }, [pendingKey]);

  // Scroll to bottom when messages change.
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ block: 'end', behavior: 'smooth' });
  }, [messages.length]);

  const persist = useCallback(
    async (next: ChatMessage[]) => {
      setMessages(next);
      try {
        await chatStore.set(chatId, next);
      } catch (e) {
        console.warn('chat persist failed', e);
      }
      const userTurnCount = next.filter(
        (m) =>
          m.role === 'user' &&
          m.content.some((b) => b.type === 'text')
      ).length;
      touchChat(chatId, userTurnCount);
    },
    [chatId, touchChat]
  );

  async function onSend() {
    if (!loaded) return;
    const text = composer.trim();
    if (!text || busy) return;
    setComposer('');
    setBusy(true);
    setError(undefined);
    const optimisticUser: ChatMessage = {
      role: 'user',
      content: [{ type: 'text', text }],
      at: Date.now(),
    };
    const baseMessages = messages;
    setMessages([...baseMessages, optimisticUser]);
    const ac = new AbortController();
    abortRef.current = ac;
    try {
      const step = await startChatTurn({
        messages: baseMessages,
        newUserText: text,
        ctx: { universeId },
        signal: ac.signal,
      });
      if (step.kind === 'error') {
        // Roll back the optimistic user message and restore the composer
        // so the user can edit + retry.
        setMessages(baseMessages);
        setComposer((c) => (c.trim() ? c : text));
        setPending([]);
        setError(step.error);
        return;
      }
      await persist(step.messages);
      if (step.kind === 'awaiting_approval') setPending(step.pending);
      else setPending([]);
      // Auto-title after first exchange — runs in background so the UI
      // doesn't block on an extra Haiku round trip.
      if (chat && chat.title === 'New chat') {
        const turnMessages = step.messages;
        void (async () => {
          const summary = await summarizeChatTitle(turnMessages);
          if (summary) {
            renameChat(chatId, summary);
            return;
          }
          // Fallback: truncated first user message.
          const firstUserText = turnMessages
            .find(
              (m): m is ChatMessage & { role: 'user' } =>
                m.role === 'user' &&
                m.content.some((b) => b.type === 'text')
            )
            ?.content.find((b) => b.type === 'text');
          if (firstUserText && firstUserText.type === 'text') {
            const t = firstUserText.text.slice(0, 60).replace(/\s+/g, ' ').trim();
            if (t) renameChat(chatId, t);
          }
        })();
      }
    } finally {
      setBusy(false);
      abortRef.current = undefined;
    }
  }

  const allResolved =
    pending.length > 0 && pending.every((p) => resolutions.has(p.tool_use_id));

  // Auto-submit when all resolutions are in.
  useEffect(() => {
    if (!allResolved || busy) return;
    const arr = pending.map((p) => ({
      tool_use_id: p.tool_use_id,
      approved: resolutions.get(p.tool_use_id)!,
    }));
    let cancelled = false;
    (async () => {
      setBusy(true);
      setError(undefined);
      const ac = new AbortController();
      abortRef.current = ac;
      try {
        const step = await resumeChatTurn({
          messages,
          resolutions: arr,
          ctx: { universeId },
          signal: ac.signal,
        });
        if (cancelled) return;
        await persist(step.messages);
        if (step.kind === 'awaiting_approval') setPending(step.pending);
        else setPending([]);
        if (step.kind === 'error') setError(step.error);
      } finally {
        if (!cancelled) {
          setBusy(false);
          abortRef.current = undefined;
        }
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allResolved]);

  function setResolution(toolUseId: string, approved: boolean) {
    setResolutions((m) => {
      const next = new Map(m);
      next.set(toolUseId, approved);
      return next;
    });
  }

  function onAbort() {
    abortRef.current?.abort();
  }

  const toolResultIndex = useMemo(() => {
    const map = new Map<string, ToolResultLookup>();
    for (const m of messages) {
      if (m.role !== 'user') continue;
      for (const b of m.content) {
        if (b.type === 'tool_result') {
          map.set(b.tool_use_id, {
            text: b.content,
            isError: b.is_error,
            approval: b.approval,
          });
        }
      }
    }
    return map;
  }, [messages]);

  if (!loaded) {
    return (
      <div className="route">
        <div className="dim">Loading chat…</div>
      </div>
    );
  }

  const pendingCount = pending.filter(
    (p) => !resolutions.has(p.tool_use_id)
  ).length;

  return (
    <div className="route chat-route">
      <div className="chat-header">
        <div className="chat-header-title">{chat?.title ?? 'Chat'}</div>
      </div>

      <div className="chat-scroll">
        {messages.length === 0 && (
          <div className="dim chat-empty">
            Ask anything about this universe — the canon, a story's tone, or a
            prompt you'd like to tweak.
          </div>
        )}
        {messages.map((m, i) => (
          <ChatMessageView
            key={i}
            message={m}
            universeId={universeId}
            toolResults={toolResultIndex}
            pendingResolutions={resolutions}
            onResolve={setResolution}
            slugPool={slugPool}
            storyBySlug={storyBySlug}
            errorBySlug={errorBySlug}
            canAuto={canAuto}
            onPickSlug={onPickSlug}
            onAutoSlug={(id) => {
              void onAutoSlug(id);
            }}
            onOpenStory={openInWorkspace}
          />
        ))}
        {busy && (
          <div className="chat-busy dim small">
            <span className="auto-dot" /> thinking…
          </div>
        )}
        {error && <div className="error small">{error}</div>}
        <div ref={bottomRef} aria-hidden="true" />
      </div>

      {pending.length > 0 && (
        <div className="approval-banner">
          {pendingCount > 0
            ? `${pendingCount} action${pendingCount === 1 ? '' : 's'} need${
                pendingCount === 1 ? 's' : ''
              } review`
            : 'Submitting…'}
        </div>
      )}

      <div className="chat-composer">
        <textarea
          rows={2}
          value={composer}
          placeholder={
            pending.length > 0
              ? 'Resolve pending actions above first.'
              : busy
                ? 'Working…'
                : 'Message…'
          }
          onChange={(e) => setComposer(e.target.value)}
          disabled={busy || pending.length > 0}
        />
        <div className="chat-composer-actions">
          {busy ? (
            <button className="ghost" onClick={onAbort}>
              Stop
            </button>
          ) : (
            <button
              className="primary"
              onClick={onSend}
              disabled={!composer.trim() || pending.length > 0}
            >
              Send
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

type ToolResultLookup = {
  text: string;
  isError?: boolean;
  approval?: 'auto' | 'applied' | 'rejected';
};

type SlugActionProps = {
  slugPool: Slug[];
  storyBySlug: Map<string, Story>;
  errorBySlug: Record<string, string>;
  canAuto: boolean;
  onPickSlug: (slugId: string) => void;
  onAutoSlug: (slugId: string) => void;
  onOpenStory: (storyId: string) => void;
};

function ChatMessageView({
  message,
  universeId,
  toolResults,
  pendingResolutions,
  onResolve,
  slugPool,
  storyBySlug,
  errorBySlug,
  canAuto,
  onPickSlug,
  onAutoSlug,
  onOpenStory,
}: {
  message: ChatMessage;
  universeId: string;
  toolResults: Map<string, ToolResultLookup>;
  pendingResolutions: Map<string, boolean>;
  onResolve: (toolUseId: string, approved: boolean) => void;
} & SlugActionProps) {
  if (message.role === 'user') {
    return <UserMessageView blocks={message.content} />;
  }
  return (
    <AssistantMessageView
      blocks={message.content}
      universeId={universeId}
      toolResults={toolResults}
      pendingResolutions={pendingResolutions}
      onResolve={onResolve}
      slugPool={slugPool}
      storyBySlug={storyBySlug}
      errorBySlug={errorBySlug}
      canAuto={canAuto}
      onPickSlug={onPickSlug}
      onAutoSlug={onAutoSlug}
      onOpenStory={onOpenStory}
    />
  );
}

function UserMessageView({ blocks }: { blocks: ChatUserBlock[] }) {
  const textBlocks = blocks.filter(
    (b): b is { type: 'text'; text: string } => b.type === 'text'
  );
  const toolResults = blocks.filter(
    (b): b is ChatToolResultBlock => b.type === 'tool_result'
  );

  // Pure tool_result messages render as inline chips beside the assistant turn,
  // so we render nothing here in that case (the ToolUse chip handles display).
  if (textBlocks.length === 0 && toolResults.length > 0) return null;

  return (
    <div className="chat-msg chat-msg-user">
      {textBlocks.map((b, i) => (
        <div key={i} className="chat-bubble user">
          {b.text}
        </div>
      ))}
    </div>
  );
}

function AssistantMessageView({
  blocks,
  universeId,
  toolResults,
  pendingResolutions,
  onResolve,
  slugPool,
  storyBySlug,
  errorBySlug,
  canAuto,
  onPickSlug,
  onAutoSlug,
  onOpenStory,
}: {
  blocks: ChatAssistantBlock[];
  universeId: string;
  toolResults: Map<string, ToolResultLookup>;
  pendingResolutions: Map<string, boolean>;
  onResolve: (toolUseId: string, approved: boolean) => void;
} & SlugActionProps) {
  return (
    <div className="chat-msg chat-msg-assistant">
      {blocks.map((b, i) => {
        if (b.type === 'text') {
          if (!b.text.trim()) return null;
          return (
            <div key={i} className="chat-bubble assistant">
              {b.text}
            </div>
          );
        }
        return (
          <ToolUseView
            key={i}
            toolUseId={b.id}
            name={b.name}
            input={b.input}
            universeId={universeId}
            result={toolResults.get(b.id)}
            resolution={pendingResolutions.get(b.id)}
            onResolve={onResolve}
            slugPool={slugPool}
            storyBySlug={storyBySlug}
            errorBySlug={errorBySlug}
            canAuto={canAuto}
            onPickSlug={onPickSlug}
            onAutoSlug={onAutoSlug}
            onOpenStory={onOpenStory}
          />
        );
      })}
    </div>
  );
}

const READ_TOOL_NAMES = new Set([
  'list_stories',
  'read_story',
  'read_canon',
  'read_prompt',
  'list_prompt_templates',
  'read_universe_meta',
]);

function ToolUseView({
  toolUseId,
  name,
  input,
  universeId,
  result,
  resolution,
  onResolve,
  slugPool,
  storyBySlug,
  errorBySlug,
  canAuto,
  onPickSlug,
  onAutoSlug,
  onOpenStory,
}: {
  toolUseId: string;
  name: string;
  input: unknown;
  universeId: string;
  result?: ToolResultLookup;
  resolution?: boolean;
  onResolve: (toolUseId: string, approved: boolean) => void;
} & SlugActionProps) {
  if (name === 'propose_slugs') {
    return (
      <SlugProposalView
        input={input}
        result={result}
        slugPool={slugPool}
        storyBySlug={storyBySlug}
        errorBySlug={errorBySlug}
        canAuto={canAuto}
        onPickSlug={onPickSlug}
        onAutoSlug={onAutoSlug}
        onOpenStory={onOpenStory}
      />
    );
  }
  if (READ_TOOL_NAMES.has(name)) {
    return (
      <ReadToolChip
        name={name}
        input={input}
        result={result}
      />
    );
  }
  // Write tools: approval card. If the tool_result already exists, the user has
  // already resolved it (or we're reloading mid-pending — same UI either way,
  // driven by `result.approval`).
  const resolvedFromResult =
    result?.approval === 'applied'
      ? true
      : result?.approval === 'rejected'
        ? false
        : undefined;
  const effectiveResolution =
    resolvedFromResult !== undefined ? resolvedFromResult : resolution;
  return (
    <ApprovalCard
      universeId={universeId}
      toolUseId={toolUseId}
      name={name}
      input={input}
      resolution={effectiveResolution}
      onResolve={(approved) => onResolve(toolUseId, approved)}
    />
  );
}

type ProposeSlugsResult = {
  batchId?: string;
  note?: string | null;
  slugIds?: string[];
};

function SlugProposalView({
  input,
  result,
  slugPool,
  storyBySlug,
  errorBySlug,
  canAuto,
  onPickSlug,
  onAutoSlug,
  onOpenStory,
}: {
  input: unknown;
  result?: ToolResultLookup;
} & SlugActionProps) {
  const inputArgs = (input ?? {}) as { note?: string };
  const note = typeof inputArgs.note === 'string' ? inputArgs.note.trim() : '';

  // While the tool is executing (no result yet), show a placeholder.
  if (!result) {
    return (
      <div className="slug-proposal">
        <div className="dim small">proposing slugs…</div>
      </div>
    );
  }

  if (result.isError) {
    return (
      <div className="tool-chip tool-chip-error">
        <div className="tool-chip-summary">
          <span className="tool-chip-icon">⚠</span>
          <span>propose_slugs failed: {result.text}</span>
        </div>
      </div>
    );
  }

  let parsed: ProposeSlugsResult | undefined;
  try {
    parsed = JSON.parse(result.text) as ProposeSlugsResult;
  } catch {
    parsed = undefined;
  }
  const ids = parsed?.slugIds ?? [];
  const slugs = ids
    .map((id) => slugPool.find((s) => s.id === id))
    .filter((s): s is Slug => !!s);

  if (slugs.length === 0) {
    return (
      <div className="slug-proposal">
        <div className="dim small">
          (these slugs are no longer in the pool — they may have been actioned
          and archived.)
        </div>
      </div>
    );
  }

  return (
    <div className="slug-proposal">
      {note && <div className="slug-proposal-note dim small">“{note}”</div>}
      {slugs.map((slug) => {
        const story = storyBySlug.get(slug.id);
        const inFlight = story?.inFlight;
        const running = inFlight !== undefined;
        const error = errorBySlug[slug.id];
        const pickedUp = !!slug.hidden && !running;
        const persistedStoryId = story?.id;
        return (
          <div key={slug.id} className="slug-card">
            <div className="slug-title">
              {slug.title}
              {slug.vibe && (
                <span className="tag tag-soft vibe-tag">
                  {VIBE_LABELS[slug.vibe]}
                </span>
              )}
            </div>
            <div className="slug-hook">{slug.hook}</div>
            {running && (
              <div className="auto-status">
                <span className="auto-dot" />
                {inFlight === 'outline' && 'Outlining…'}
                {inFlight === 'prose' && 'Writing…'}
                {inFlight === 'tts' && 'Recording…'}
              </div>
            )}
            {!running && error && (
              <div className="error small">Auto-build failed: {error}</div>
            )}
            <div className="slug-actions">
              {pickedUp ? (
                <>
                  <span className="dim small">picked up</span>
                  {persistedStoryId && (
                    <button
                      className="ghost"
                      onClick={() => onOpenStory(persistedStoryId)}
                    >
                      Open in workspace
                    </button>
                  )}
                </>
              ) : (
                <>
                  {!running && (
                    <button
                      className="primary"
                      onClick={() => onAutoSlug(slug.id)}
                      disabled={!canAuto}
                      title={
                        !canAuto
                          ? 'Configure sync (Settings) and set a default voice (Universe settings)'
                          : undefined
                      }
                    >
                      {error ? 'Retry auto' : 'Auto'}
                    </button>
                  )}
                  {!running && (
                    <button onClick={() => onPickSlug(slug.id)}>Edit</button>
                  )}
                  {!running && error && persistedStoryId && (
                    <button
                      className="ghost"
                      onClick={() => onOpenStory(persistedStoryId)}
                    >
                      Open in workspace
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function ReadToolChip({
  name,
  input,
  result,
}: {
  name: string;
  input: unknown;
  result?: ToolResultLookup;
}) {
  const [open, setOpen] = useState(false);
  return (
    <div
      className={`tool-chip ${result?.isError ? 'tool-chip-error' : ''} ${
        open ? 'open' : ''
      }`}
    >
      <button
        type="button"
        className="tool-chip-summary"
        onClick={() => setOpen((o) => !o)}
      >
        <span className="tool-chip-icon">📖</span>
        <span>{summarizeToolUse(name, input)}</span>
        {!result && <span className="dim small">…</span>}
      </button>
      {open && result && (
        <pre className="tool-chip-body">{result.text}</pre>
      )}
    </div>
  );
}
